import { useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { getAuthToken, getCsrfToken } from '../services/api'
import { useToast } from '../components/Toast'

// Web Worker support (with fallback)
let AIWorker = null
let workerSupported = false

try {
  // Try to create Web Worker
  if (typeof Worker !== 'undefined') {
    try {
      AIWorker = new Worker(new URL('../workers/aiWorker.js', import.meta.url), { type: 'module' })
      workerSupported = true
    } catch (e) {
      console.warn('[useOrionAI] Web Worker creation failed, using fallback:', e)
      workerSupported = false
    }
  }
} catch (e) {
  console.warn('[useOrionAI] Web Workers not supported, using fallback')
  workerSupported = false
}

/**
 * Circuit Breaker Pattern voor AI Service
 * Voorkomt dat de app vastloopt bij AI failures
 */
class CircuitBreaker {
  constructor(threshold = 3, timeout = 60000) {
    this.failureCount = 0
    this.threshold = threshold
    this.timeout = timeout
    this.state = 'CLOSED' // CLOSED, OPEN, HALF_OPEN
    this.nextAttempt = null
  }

  recordSuccess() {
    this.failureCount = 0
    this.state = 'CLOSED'
    this.nextAttempt = null
  }

  recordFailure() {
    this.failureCount++
    if (this.failureCount >= this.threshold) {
      this.state = 'OPEN'
      this.nextAttempt = Date.now() + this.timeout
    }
  }

  canAttempt() {
    if (this.state === 'CLOSED') return true
    if (this.state === 'OPEN') {
      if (Date.now() >= this.nextAttempt) {
        this.state = 'HALF_OPEN'
        return true
      }
      return false
    }
    // HALF_OPEN
    return true
  }

  getStatus() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      isAvailable: this.canAttempt(),
    }
  }
}

/**
 * Parse Generative UI commands uit LLM response.
 * Zoekt naar JSON blokken: {"commands":[{"type":"...","label":"...","payload":{...}}]}
 */
function parseGenerativeUICommands(text) {
  if (!text || typeof text !== 'string') return []
  try {
    const jsonMatch = text.match(/\{[\s\S]*"commands"[\s\S]*\}/)
    if (!jsonMatch) return []
    const parsed = JSON.parse(jsonMatch[0])
    return Array.isArray(parsed.commands) ? parsed.commands : []
  } catch {
    return []
  }
}

/**
 * useOrionAI Hook
 *
 * Provides:
 * - Chat functionality (reactive assistant)
 * - SSE streaming met typing effect
 * - Generative UI (actionable buttons)
 * - Context awareness
 * - Circuit breaker for resilience
 * - Action execution bridge
 */
// Rood = er is niets bruikbaars. Amber (de mededeling) = bruikbaar maar onvolledig.
// Rood dat ook bij bruikbare antwoorden verschijnt, leert de gebruiker rood te
// negeren, en dan valt een echte storing niet meer op.
function bepaalIsError(result) {
  if (result?.volledig_mislukt) return true
  // Antwoorden zonder tools (fallback-pad) dragen het veld niet; daar is
  // success nog steeds het enige signaal dat er niets bruikbaars is.
  if (result?.volledig_mislukt === undefined) return !result?.success
  return false
}

export function useOrionAI(context = {}) {
  const [messages, setMessages] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [aiAvailable, setAiAvailable] = useState(true)
  const circuitBreakerRef = useRef(new CircuitBreaker(3, 60000))
  const toast = useToast()
  const queryClient = useQueryClient()
  const workerRef = useRef(AIWorker)
  const pendingRequestsRef = useRef(new Map())
  const requestIdCounterRef = useRef(0)

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Hallo! Ik ben de Orion AI Assistant. Hoe kan ik je helpen?\n\nIk kan je helpen met:\n• Vragen over je project\n• Business logic validatie\n• Automatische correcties',
        isError: false,
        timestamp: new Date().toISOString(),
      }])
    }
  }, [])

  // Check circuit breaker status
  useEffect(() => {
    const status = circuitBreakerRef.current.getStatus()
    setAiAvailable(status.isAvailable)
  }, [messages])

  // Worker message listener: ontvang COMMAND_RESULT, STREAM_CHUNK, ERROR van AI worker
  useEffect(() => {
    const worker = workerRef.current
    if (!worker || !workerSupported) return

    const handleMessage = (event) => {
      const { type, requestId, result, error, content } = event.data || {}
      if (!requestId || !String(requestId).startsWith('cmd_')) return

      const pending = pendingRequestsRef.current.get(requestId)
      if (!pending) return

      if (type === 'STREAM_CHUNK') {
        // Streaming: stuur chunk naar onChunk callback
        if (pending.onChunk) pending.onChunk(content)
        return
      }

      pendingRequestsRef.current.delete(requestId)

      if (type === 'COMMAND_RESULT') {
        pending.resolve(result)
      } else if (type === 'ERROR') {
        pending.reject(new Error(error || 'Unknown error'))
      }
    }

    worker.addEventListener('message', handleMessage)
    return () => worker.removeEventListener('message', handleMessage)
  }, [])

  /**
   * Send message to AI (async, non-blocking)
   */
  const sendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isProcessing) return

    const circuitBreaker = circuitBreakerRef.current
    
    // Check circuit breaker
    if (!circuitBreaker.canAttempt()) {
      const errorMsg = 'AI service is tijdelijk niet beschikbaar. Probeer het over een minuut opnieuw.'
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMsg,
        isError: true,
        timestamp: new Date().toISOString(),
      }])
      toast.error(errorMsg)
      return
    }

    setIsProcessing(true)

    // Add user message
    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMsg])

    try {
      let result
      const useStream = !!(workerRef.current && workerSupported)
      const streamMessageId = useStream ? `stream_${Date.now()}` : null

      if (useStream) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '',
          isError: false,
          timestamp: new Date().toISOString(),
          isStreaming: true,
          _streamId: streamMessageId,
        }])
      }

      const onChunk = (chunk) => {
        setMessages(prev => {
          const idx = prev.findIndex(m => m._streamId === streamMessageId)
          if (idx === -1) return prev
          const updated = [...prev]
          updated[idx] = { ...updated[idx], content: (updated[idx].content || '') + chunk }
          return updated
        })
      }

      // Use Web Worker met streaming indien beschikbaar
      if (workerRef.current && workerSupported) {
        const requestId = `cmd_${++requestIdCounterRef.current}`

        const workerPromise = new Promise((resolve, reject) => {
          pendingRequestsRef.current.set(requestId, { resolve, reject, onChunk })

          const token = getAuthToken()
          const csrfToken = getCsrfToken()
          workerRef.current.postMessage({
            type: 'PROCESS_COMMAND_STREAM',
            requestId,
            payload: {
              command: userMessage,
              context: context,
              apiUrl: api.defaults.baseURL || '/api/v1',
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
                // Een Web Worker kan document.cookie niet lezen, en de axios-interceptor
                // (die dit normaal toevoegt) draait hier niet. Zonder deze header weigert
                // de CSRF-middleware de POST met 403 "CSRF token ontbreekt".
                ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
              },
            },
          })
        })

        result = await Promise.race([
          workerPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('AI request timeout')), 60000)
          ),
        ])
      } else {
        // Fallback: direct API call
        const response = await Promise.race([
          api.post('ai/process', {
            command: userMessage,
            context: context,
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('AI request timeout')), 30000)
          ),
        ])
        result = response.data
      }

      // Record success
      circuitBreaker.recordSuccess()

      // Vervang streaming message door definitieve (met actions, generative UI)
      const finalContent = result.message || 'Geen bericht'
      const parsedCommands = parseGenerativeUICommands(finalContent)

      setMessages(prev => {
        const idx = streamMessageId ? prev.findIndex(m => m._streamId === streamMessageId) : -1
        if (idx === -1) {
          return [...prev, {
            role: 'assistant',
            content: finalContent,
            isError: bepaalIsError(result),
            timestamp: new Date().toISOString(),
            actions: result.actions || [],
            generativeCommands: parsedCommands,
            created_objects: result.created_objects || {},
            next_suggestions: result.next_suggestions || [],
            // Komt letterlijk uit de backend, niet uit het model. Apart houden van
            // content, zodat de weergave het onderscheid kan tonen.
            mededeling: result.mededeling || null,
          }]
        }
        const updated = [...prev]
        updated[idx] = {
          ...updated[idx],
          content: finalContent,
          isStreaming: false,
          _streamId: undefined,
          isError: bepaalIsError(result),
          actions: result.actions || [],
          generativeCommands: parsedCommands,
          created_objects: result.created_objects || {},
          next_suggestions: result.next_suggestions || [],
          mededeling: result.mededeling || null,
        }
        return updated
      })

      // Execute actions (navigate, etc.)
      if (result.actions && Array.isArray(result.actions)) {
        for (const action of result.actions) {
          await executeAction(action)
        }
      }

      // Losgekoppeld van result.success: sinds de wrapper success afleidt, kan een
      // ronde deels slagen -- tool A maakt een klant aan, tool B faalt. Bleef dit
      // aan success hangen, dan was het object wel aangemaakt maar bleef de lijst
      // stil verouderd, en de mededeling zegt daar niets over. Wat is aangemaakt,
      // is aangemaakt.
      if (result.created_objects) {
        // Invalidate relevant queries
        if (result.created_objects.klant_id) {
          queryClient.invalidateQueries({ queryKey: ['klanten'] })
        }
        if (result.created_objects.project_id) {
          queryClient.invalidateQueries({ queryKey: ['projecten'] })
        }
        if (result.created_objects.offerte_id) {
          queryClient.invalidateQueries({ queryKey: ['offertes'] })
        }
      }

      return result
    } catch (error) {
      console.error('AI Assistant error:', error)
      
      // Record failure
      circuitBreaker.recordFailure()
      
      const errorMessage = error.response?.data?.detail || error.message || 'Fout bij verwerken commando'
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Fout: ${errorMessage}`,
        isError: true,
        timestamp: new Date().toISOString(),
      }])

      // Update availability status
      const status = circuitBreaker.getStatus()
      setAiAvailable(status.isAvailable)

      if (!status.isAvailable) {
        toast.error('AI service is tijdelijk niet beschikbaar')
      } else {
        toast.error(errorMessage)
      }

      throw error
    } finally {
      setIsProcessing(false)
    }
  }, [isProcessing, context, toast, queryClient])

  /**
   * Execute a specific action (bridge to state updates)
   */
  const executeAction = useCallback(async (action, params = {}) => {
    if (!action || !action.type) {
      console.warn('Invalid action:', action)
      return
    }

    const { type, payload } = action

    switch (type) {
      case 'UPDATE_CABINET_DIMENSION':
        // This would typically update a cabinet config in state
        // For now, we'll emit an event that components can listen to
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload, params },
        }))
        break

      case 'CHANGE_MATERIAL':
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload, params },
        }))
        break

      case 'REMOVE_EDGEBANDING':
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload, params },
        }))
        break

      case 'SET_MIN_DEPTH':
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload, params },
        }))
        break

      case 'APPLY_AUTOFIT_DEDUCTION':
      case 'CHANGE_EDGE_BAND_WIDTH':
      case 'CONFIRM_GRAIN_DIRECTION':
      case 'CHANGE_BACK_PANEL_TO_8MM':
      case 'CONFIGURE_DRAWERS':
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload: { ...payload, ...params }, params },
        }))
        break

      case 'NAVIGATE':
        if (payload && payload.url) {
          // Use React Router navigate if available, otherwise fallback to window.location
          try {
            const { useNavigate } = await import('react-router-dom')
            // Note: This won't work in callback - navigate should be passed from component
            // Fallback to window.location for now
            window.location.href = payload.url
          } catch {
            window.location.href = payload.url
          }
        }
        break

      default:
        // Dispatch onbekende types voor Guardian-fixes zodat listeners kunnen reageren
        window.dispatchEvent(new CustomEvent('orion-ai-action', {
          detail: { type, payload: { ...payload, ...params }, params },
        }))
    }
  }, [])

  /**
   * Execute AI command (for Guardian interventions)
   * Can accept either a string command or a parsed action object
   */
  const executeAICommand = useCallback(async (command, params = {}) => {
    try {
      // If command is a string, send it as a message
      if (typeof command === 'string') {
        const result = await sendMessage(command)
        
        // Execute any actions returned
        if (result?.actions && Array.isArray(result.actions)) {
          for (const action of result.actions) {
            await executeAction(action, params)
          }
        }

        return result
      } else {
        // If command is an action object, execute it directly
        await executeAction(command, params)
        return { success: true, message: 'Actie uitgevoerd' }
      }
    } catch (error) {
      console.error('Error executing AI command:', error)
      throw error
    }
  }, [sendMessage, executeAction])

  /**
   * Clear conversation history
   */
  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  /**
   * Voeg proactieve AI-bericht toe (Phase 2: PDF import handoff).
   * Gebruikt voor Guardian-triggered berichten met Generative UI knoppen.
   * Ondersteunt unmapped_items voor dropdown (material_id null in material_calculator_ingest).
   * Ondersteunt whatsapp_receipts voor bonnen-toewijzing (WhatsApp Receipt Scanner).
   *
   * @param {string} content - Berichttekst
   * @param {Object} options - { commands, actions, unmapped_items, project_id, whatsapp_receipts }
   */
  const addProactiveMessage = useCallback((content, options = {}) => {
    const msg = {
      role: 'assistant',
      content: content || '',
      generativeCommands: options.commands || options.generativeCommands || [],
      actions: options.actions || [],
      unmapped_items: options.unmapped_items || [],
      project_id: options.project_id,
      whatsapp_receipts: options.whatsapp_receipts || [],
      isError: false,
      timestamp: new Date().toISOString(),
      isProactive: true,
    }
    setMessages((prev) => [...prev, msg])
  }, [])

  return {
    messages,
    sendMessage,
    executeAICommand,
    executeAction,
    clearMessages,
    addProactiveMessage,
    isProcessing,
    aiAvailable,
    circuitBreakerStatus: circuitBreakerRef.current.getStatus(),
  }
}
